import { z } from 'zod';
import {
  ACCOUNT_TYPES,
  ACTIONS,
  APP_APPROVALS,
  APP_CATEGORIES,
  BROWSERS,
  DECISIONS,
  SENSITIVITY_LEVELS,
  SEVERITIES,
} from '../types.js';

/**
 * Policy authoring + distribution schemas. Every collection is bounded so that a
 * malicious or corrupted payload cannot make the extension or server do unbounded work.
 */

const id = z.string().min(1).max(64).regex(/^[A-Za-z0-9_.:-]+$/);
const idList = (max = 500) => z.array(id).max(max);

const setCondition = <T extends z.ZodTypeAny>(item: T, max = 500) =>
  z.object({ op: z.enum(['in', 'not_in']), values: z.array(item).min(1).max(max) });

export const ruleConditionsSchema = z
  .object({
    users: setCondition(id).optional(),
    groups: setCondition(id).optional(),
    deviceManaged: z.enum(['managed', 'unmanaged']).optional(),
    browsers: z.array(z.enum(BROWSERS)).min(1).max(BROWSERS.length).optional(),
    apps: setCondition(id).optional(),
    appCategories: setCondition(z.enum(APP_CATEGORIES), 20).optional(),
    appApproval: z.array(z.enum(APP_APPROVALS)).min(1).max(3).optional(),
    accountTypes: z.array(z.enum(ACCOUNT_TYPES)).min(1).max(3).optional(),
    actions: z.array(z.enum(ACTIONS)).min(1).max(ACTIONS.length).optional(),
    data: z
      .object({
        classifications: idList(100).optional(),
        minSensitivity: z.enum(SENSITIVITY_LEVELS).optional(),
        /** Also match when content could not be fully inspected (unreadable file type, or truncated). */
        orUninspectable: z.boolean().optional(),
      })
      .refine((d) => (d.classifications?.length ?? 0) > 0 || d.minSensitivity || d.orUninspectable, {
        message: 'data condition needs classifications, a minimum sensitivity, or uninspectable content',
      })
      .optional(),
    file: z
      .object({
        extensions: z.array(z.string().min(1).max(16).regex(/^[a-z0-9]+$/)).max(100).optional(),
        minSizeBytes: z.number().int().min(0).max(1e12).optional(),
      })
      .optional(),
  })
  .strict();
export type RuleConditions = z.infer<typeof ruleConditionsSchema>;

export const ruleOptionsSchema = z
  .object({
    userMessage: z.string().max(500).optional(),
    alert: z.boolean().default(false),
    severity: z.enum(SEVERITIES).default('medium'),
    notifyIntegrations: z.boolean().default(false),
    justificationPrompt: z.string().max(300).optional(),
    /** Minutes a WARN/JUSTIFY acknowledgement is remembered for the same app+rule. */
    acknowledgeMinutes: z.number().int().min(0).max(24 * 60).default(10),
  })
  .strict();

export const policyRuleSchema = z
  .object({
    id,
    name: z.string().min(1).max(120),
    enabled: z.boolean(),
    /** Lower number = evaluated as more specific when actions tie. */
    priority: z.number().int().min(0).max(100000),
    conditions: ruleConditionsSchema,
    action: z.enum(DECISIONS),
    options: ruleOptionsSchema,
  })
  .strict();
export type PolicyRule = z.infer<typeof policyRuleSchema>;

export const policyExceptionSchema = z
  .object({
    id,
    /** Rules this exception exempts. Empty = invalid (exceptions must be targeted). */
    ruleIds: idList(100).min(1),
    userIds: idList().default([]),
    groupIds: idList().default([]),
    appIds: idList().default([]),
    expiresAt: z.string().datetime().nullable(),
  })
  .strict();
export type PolicyException = z.infer<typeof policyExceptionSchema>;

const classificationCriterionSchema = z
  .object({
    detector: id,
    minCount: z.number().int().min(1).max(10000),
  })
  .strict();

export const classificationSchema = z
  .object({
    id,
    name: z.string().min(1).max(120),
    sensitivity: z.enum(SENSITIVITY_LEVELS),
    /** Classification applies when ANY criterion is satisfied. */
    criteria: z.array(classificationCriterionSchema).min(1).max(50),
  })
  .strict();
export type Classification = z.infer<typeof classificationSchema>;

export const customDetectorSchema = z.discriminatedUnion('type', [
  z
    .object({
      id,
      name: z.string().min(1).max(120),
      type: z.literal('regex'),
      pattern: z.string().min(1).max(300),
      caseInsensitive: z.boolean().default(false),
    })
    .strict(),
  z
    .object({
      id,
      name: z.string().min(1).max(120),
      type: z.literal('dictionary'),
      terms: z.array(z.string().min(2).max(100)).min(1).max(1000),
      caseInsensitive: z.boolean().default(true),
      wholeWord: z.boolean().default(true),
    })
    .strict(),
]);
export type CustomDetector = z.infer<typeof customDetectorSchema>;

const hostPattern = z
  .string()
  .min(1)
  .max(253)
  .regex(/^(\*\.)?[a-z0-9-]+(\.[a-z0-9-]+)*$/, 'hostname or *.suffix');

export const bundleAppSchema = z
  .object({
    id,
    name: z.string().min(1).max(120),
    hosts: z.array(hostPattern).min(1).max(50),
    category: z.enum(APP_CATEGORIES),
    approval: z.enum(APP_APPROVALS),
    identityProvider: z.string().max(40).nullable().default(null),
    /** Optional admin-supplied CSS selector whose text contains the signed-in account email. */
    accountSelector: z.string().max(200).nullable().default(null),
  })
  .strict();
export type BundleApp = z.infer<typeof bundleAppSchema>;

const bundleSettingsSchema = z
  .object({
    corporateDomains: z.array(z.string().min(3).max(253)).max(100),
    /** SharePoint/OneDrive tenant prefixes (e.g. "acme" for acme.sharepoint.com). */
    microsoftTenantPrefixes: z.array(z.string().min(1).max(63)).max(50).default([]),
    discoveryScope: z.enum(['all', 'catalog_only']).default('all'),
    collectFileNames: z.boolean().default(true),
    collectPromptMetadata: z.boolean().default(true),
    collectAccountIdentifiers: z.enum(['none', 'corporate_only', 'all']).default('corporate_only'),
    heartbeatMinutes: z.number().int().min(1).max(1440).default(5),
    policyCheckMinutes: z.number().int().min(1).max(1440).default(5),
    staleAfterHours: z.number().int().min(1).max(24 * 90).default(72),
    supportContact: z.string().max(200).default(''),
    maxInspectBytes: z.number().int().min(1024).max(50 * 1024 * 1024).default(10 * 1024 * 1024),
  })
  .strict();
export type BundleSettings = z.infer<typeof bundleSettingsSchema>;

export const BUNDLE_SCHEMA_VERSION = 1;

export const policyBundleSchema = z
  .object({
    schemaVersion: z.literal(BUNDLE_SCHEMA_VERSION),
    minExtensionVersion: z.string().regex(/^\d+\.\d+\.\d+$/),
    orgId: id,
    orgName: z.string().max(120),
    version: z.number().int().min(0),
    createdAt: z.string().datetime(),
    /** Personalised per device: the resolved user and groups the server believes are using it. */
    subject: z
      .object({
        userId: id.nullable(),
        groupIds: idList(1000),
        deviceManaged: z.boolean(),
        /**
         * Whether the server has *verified* the user (e.g. an administrator confirmed the
         * device's user). Unverified identities never receive relaxations: user/group-scoped
         * exceptions and "everyone except" conditions are not applied to them.
         */
        identityVerified: z.boolean().default(false),
        /**
         * When the identity evidence behind `identityVerified` stops being trusted (e.g. an
         * Entra ID sign-in older than the organisation's trust lifetime). After this moment the
         * device treats the identity as unverified even while offline. Null = no time limit
         * (e.g. an administrator's confirmation) or not verified.
         */
        identityTrustExpiresAt: z.string().datetime().nullable().default(null),
      })
      .strict(),
    settings: bundleSettingsSchema,
    rules: z.array(policyRuleSchema).max(500),
    exceptions: z.array(policyExceptionSchema).max(1000),
    classifications: z.array(classificationSchema).max(200),
    detectors: z.array(customDetectorSchema).max(200),
    apps: z.array(bundleAppSchema).max(5000),
  })
  .strict();
export type PolicyBundle = z.infer<typeof policyBundleSchema>;

/** Signed envelope: the signature covers the exact `payload` string, so no JSON
 * canonicalisation is required on either side. */
export const signedBundleSchema = z
  .object({
    payload: z.string().max(4 * 1024 * 1024),
    signature: z.string().max(200),
    keyId: z.string().max(64),
    alg: z.literal('ES256'),
  })
  .strict();
export type SignedBundle = z.infer<typeof signedBundleSchema>;

const keyIdSchema = z.string().min(1).max(64).regex(/^[A-Za-z0-9._:-]+$/);

/** A policy-signing public key the device may trust: base64 SPKI (P-256) plus its key id. */
export const trustedKeySchema = z.object({ keyId: z.string().max(64), publicKey: z.string().min(40).max(400) }).strict();
export type TrustedKey = z.infer<typeof trustedKeySchema>;

/**
 * Keyring statement (key rotation). The complete set of keys the deployment vouches for;
 * it replaces the device's trusted set when signed by a key the device already trusts.
 * Delivered next to (not inside) the policy bundle because the bundle schema is strict
 * and older extensions would reject an unknown field.
 */
export const policyKeyringSchema = z
  .object({
    schemaVersion: z.literal(1),
    type: z.literal('ward.policy-keyring'),
    issuedAt: z.string().datetime(),
    keys: z.array(z.object({ keyId: keyIdSchema, publicKey: z.string().min(40).max(400) }).strict()).min(1).max(8),
  })
  .strict();
export type PolicyKeyring = z.infer<typeof policyKeyringSchema>;

export const signedKeyringSchema = z
  .object({
    payload: z.string().max(8 * 1024),
    signatures: z.array(z.object({ keyId: z.string().max(64), signature: z.string().max(200) }).strict()).min(1).max(8),
    alg: z.literal('ES256'),
  })
  .strict();
export type SignedKeyring = z.infer<typeof signedKeyringSchema>;
